import { ReactNode } from "react";
import { centsToDollarsFormatted } from "@/lib/currencyHelpers";

export interface TableFooterLineItem {
  label: string | ReactNode;
  value: string | ReactNode | number | null;
  isEmphasized?: boolean;
  className?: string;
}

interface BuildTableFooterLineItemsProps {
  items: TableFooterLineItem[];
  showTopBorder?: boolean;
  isSingle?: boolean;
  amountWidth?: string;
}

/**
 * Builds consistent footer line items for tables with proper spacing, typography, and alignment.
 *
 * **Variants:**
 * - `isSingle`: Single item with extra bottom padding (`pb-3`)
 * - Group: Multiple items with top padding (`pt-3`) and spacing between items (`pb-2`)
 * - `showTopBorder`: Adds a border separator above the items
 * - `isEmphasized`: Applies emphasized typography (semibold) to both label and value
 *
 * **Styling:**
 * - Labels use `text-body-medium-semibold` when `isEmphasized` is true, otherwise `text-body-medium`
 * - Values are right-aligned with fixed width (default `w-[100px]`)
 * - All items are right-aligned using flexbox
 * - If `value` is a number (cents), it will be automatically formatted using `centsToDollarsFormatted`
 * - If `value` is a string or ReactNode, it will be displayed as-is
 */
export function buildTableFooterLineItems({
  items,
  showTopBorder = false,
  isSingle = false,
  amountWidth = "w-[100px]"
}: BuildTableFooterLineItemsProps): ReactNode {
  if (items.length === 0) return null;

  const renderLineItem = (item: TableFooterLineItem, isLast: boolean, index?: number) => {
    const labelClassName = item.isEmphasized ? "text-body-medium-semibold" : "text-body-medium";
    const valueClassName = item.isEmphasized ? "text-body-medium-semibold" : "text-body-medium";

    const displayValue =
      typeof item.value === "number" || item.value === null ? centsToDollarsFormatted(item.value) : item.value;

    return (
      <div
        key={index !== undefined ? `footer-item-${index}` : `footer-item-${item.label}-${item.value}`}
        className={`flex justify-end ${isSingle ? "" : isLast ? "" : "pb-2"}`}
      >
        <h5 className={labelClassName}>{item.label}</h5>
        <div className={`flex justify-end ${valueClassName} ${amountWidth} ${item.className || ""}`}>
          {displayValue}
        </div>
      </div>
    );
  };

  if (isSingle) {
    return (
      <>
        {showTopBorder && <div className="border-t -ml-3 w-[calc(100%+1.5rem)]" />}
        <div className="pb-3">{renderLineItem(items[0], true, 0)}</div>
      </>
    );
  }

  return (
    <>
      {showTopBorder && <div className="border-t -ml-3 w-[calc(100%+1.5rem)]" />}
      <div className={`${showTopBorder ? "pt-3" : ""} flex flex-col justify-end`}>
        {items.map((item, index) => renderLineItem(item, index === items.length - 1, index))}
      </div>
    </>
  );
}
